/**
 * Video player.
 *
 * Adaptive playback via hls.js (Safari uses its native HLS), falling back to a
 * progressive MP4 if neither is available. The controls are hand-rolled rather
 * than the browser default so the experience is identical everywhere and can
 * carry the deck's styling: scrub bar with buffered ranges and hover preview,
 * quality and speed menus, volume, PiP, fullscreen, and the keyboard shortcuts
 * people already have in their fingers from YouTube.
 */
// hls.js is ~200KB gzipped and only needed once a video is actually opened, so
// it is pulled in dynamically — the deck itself loads without paying for it.
import type HlsJs from 'hls.js';
import { chooseSource, hasMediaSource } from './media-support.ts';

export interface Rendition {
  label: string;
  width: number;
  height: number;
  bitrateK: number;
}

export interface VideoAsset {
  id: string;
  title: string;
  subtitle?: string;
  duration: number;
  width: number;
  height: number;
  aspect: number;
  portrait: boolean;
  poster: string;
  posterLqip: string;
  hls: string;
  mp4: string;
  renditions: Rendition[];
}

export interface PlayerHooks {
  mediaUrl(rel: string): string;
  onPlay?(id: string): void;
  /** Called with additional watched seconds since the last report. */
  onWatched?(id: string, seconds: number): void;
}

const ICONS = {
  play: '<svg viewBox="0 0 24 24"><path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11-6.86a1 1 0 0 0 0-1.72l-11-6.86A1 1 0 0 0 8 5.14z"/></svg>',
  pause: '<svg viewBox="0 0 24 24"><path d="M7 4h3.5v16H7zM13.5 4H17v16h-3.5z"/></svg>',
  replay:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/></svg>',
  volHigh:
    '<svg viewBox="0 0 24 24"><path d="M4 9v6h4l5 4V5L8 9H4z"/><path d="M16.5 8.5a5 5 0 0 1 0 7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><path d="M19 6a8.5 8.5 0 0 1 0 12" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
  volLow:
    '<svg viewBox="0 0 24 24"><path d="M4 9v6h4l5 4V5L8 9H4z"/><path d="M16.5 8.5a5 5 0 0 1 0 7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
  volMute:
    '<svg viewBox="0 0 24 24"><path d="M4 9v6h4l5 4V5L8 9H4z"/><path d="M16 9.5l5 5M21 9.5l-5 5" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/></svg>',
  settings:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 9 19.4a1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.6 9a1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z"/></svg>',
  pip: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linejoin="round"><rect x="2.5" y="4.5" width="19" height="15" rx="2"/><rect x="12" y="11" width="8" height="6.5" rx="1" fill="currentColor" stroke="none"/></svg>',
  full: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3"/></svg>',
  exitFull:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8h3a2 2 0 0 0 2-2V3M21 8h-3a2 2 0 0 1-2-2V3M3 16h3a2 2 0 0 1 2 2v3M21 16h-3a2 2 0 0 0-2 2v3"/></svg>',
  close:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 5l14 14M19 5L5 19"/></svg>',
};

function fmtTime(t: number): string {
  if (!Number.isFinite(t) || t < 0) t = 0;
  const s = Math.floor(t % 60);
  const m = Math.floor((t / 60) % 60);
  const h = Math.floor(t / 3600);
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  html?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (className) n.className = className;
  if (html !== undefined) n.innerHTML = html;
  return n;
}


const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

export class Player {
  private overlay: HTMLElement;
  private box!: HTMLElement;
  private video!: HTMLVideoElement;
  private poster!: HTMLImageElement;
  private bigPlay!: HTMLElement;
  private spinner!: HTMLElement;
  private playBtn!: HTMLButtonElement;
  private muteBtn!: HTMLButtonElement;
  private volInput!: HTMLInputElement;
  private timeLabel!: HTMLElement;
  private scrub!: HTMLElement;
  private played!: HTMLElement;
  private buffered!: HTMLElement;
  private hoverBar!: HTMLElement;
  private knob!: HTMLElement;
  private tip!: HTMLElement;
  private menu!: HTMLElement;
  private menuBtn!: HTMLButtonElement;
  private fsBtn!: HTMLButtonElement;

  private hls: HlsJs | null = null;
  private hideTimer = 0;
  private watchAccum = 0;
  private lastTick = 0;
  private reportTimer = 0;
  private disposed = false;
  private quality: 'auto' | number = 'auto';
  private onKey!: (e: KeyboardEvent) => void;

  constructor(
    private readonly asset: VideoAsset,
    private readonly hooks: PlayerHooks,
    private readonly onClose: () => void,
  ) {
    this.overlay = el('div', 'player-overlay');
    this.overlay.setAttribute('role', 'dialog');
    this.overlay.setAttribute('aria-modal', 'true');
    this.overlay.setAttribute('aria-label', `${asset.title} — video`);
    this.build();
    document.body.appendChild(this.overlay);
    this.bindKeys();
    void this.attachMedia();
  }

  // -- construction --------------------------------------------------------

  private build(): void {
    const shell = el('div', 'player-shell');

    const head = el('div', 'player-head');
    const meta = el('div', 'player-meta');
    const title = el('h2', 'player-title');
    title.textContent = this.asset.title;
    meta.appendChild(title);
    if (this.asset.subtitle) {
      const sub = el('p', 'player-sub');
      sub.textContent = this.asset.subtitle;
      meta.appendChild(sub);
    }
    head.appendChild(meta);
    head.appendChild(el('div', 'control-spacer'));
    const closeBtn = el('button', 'icon-btn', ICONS.close) as HTMLButtonElement;
    closeBtn.setAttribute('aria-label', 'Close video (Esc)');
    closeBtn.addEventListener('click', () => this.close());
    head.appendChild(closeBtn);
    shell.appendChild(head);

    this.box = el('div', `player-box${this.asset.portrait ? ' portrait' : ''}`);
    this.box.style.setProperty('--v-aspect', String(this.asset.aspect));

    this.video = document.createElement('video');
    this.video.playsInline = true;
    this.video.preload = 'metadata';
    this.video.setAttribute('crossorigin', 'use-credentials');
    this.box.appendChild(this.video);

    this.poster = document.createElement('img');
    this.poster.className = 'player-poster';
    this.poster.alt = '';
    this.poster.src = this.hooks.mediaUrl(this.asset.poster);
    this.box.appendChild(this.poster);

    this.spinner = el('div', 'spinner');
    this.box.appendChild(this.spinner);

    this.bigPlay = el('button', 'big-play', `<div>${ICONS.play}</div>`);
    this.bigPlay.setAttribute('aria-label', 'Play');
    this.bigPlay.addEventListener('click', () => this.toggle());
    this.box.appendChild(this.bigPlay);

    this.box.appendChild(this.buildControls());
    shell.appendChild(this.box);

    const foot = el('div', 'player-foot');
    foot.innerHTML =
      `<span>Press <span class="kbd">space</span> play · <span class="kbd">←</span><span class="kbd">→</span> seek · ` +
      `<span class="kbd">f</span> fullscreen · <span class="kbd">esc</span> close</span>`;
    const dur = el('span');
    dur.textContent = fmtTime(this.asset.duration);
    foot.appendChild(dur);
    shell.appendChild(foot);

    this.overlay.appendChild(shell);

    // Clicking the backdrop (but not the player) closes.
    this.overlay.addEventListener('mousedown', (e) => {
      if (e.target === this.overlay) this.close();
    });

    this.box.addEventListener('click', (e) => {
      if (e.target === this.video || e.target === this.box) this.toggle();
    });
    this.box.addEventListener('dblclick', (e) => {
      if (e.target === this.video || e.target === this.box) void this.toggleFullscreen();
    });
    this.box.addEventListener('mousemove', () => this.nudgeUi());
    this.box.addEventListener('mouseleave', () => {
      if (!this.video.paused) this.box.classList.add('hide-ui');
    });
  }

  private buildControls(): HTMLElement {
    const c = el('div', 'controls');

    // --- scrub bar ---
    this.scrub = el('div', 'scrub');
    this.scrub.setAttribute('role', 'slider');
    this.scrub.setAttribute('aria-label', 'Seek');
    this.scrub.tabIndex = 0;
    const track = el('div', 'scrub-track');
    this.buffered = el('div', 'scrub-buffered');
    this.hoverBar = el('div', 'scrub-hover');
    this.played = el('div', 'scrub-played');
    this.knob = el('div', 'scrub-knob');
    track.append(this.buffered, this.hoverBar, this.played, this.knob);
    this.tip = el('div', 'scrub-tip');
    this.scrub.append(track, this.tip);
    c.appendChild(this.scrub);
    this.bindScrub();

    // --- button row ---
    const row = el('div', 'control-row');

    this.playBtn = el('button', 'cbtn', ICONS.play) as HTMLButtonElement;
    this.playBtn.setAttribute('aria-label', 'Play');
    this.playBtn.addEventListener('click', () => this.toggle());
    row.appendChild(this.playBtn);

    const volWrap = el('div', 'volume-wrap');
    this.muteBtn = el('button', 'cbtn', ICONS.volHigh) as HTMLButtonElement;
    this.muteBtn.setAttribute('aria-label', 'Mute');
    this.muteBtn.addEventListener('click', () => this.toggleMute());
    const volBox = el('div', 'volume');
    this.volInput = document.createElement('input');
    this.volInput.type = 'range';
    this.volInput.min = '0';
    this.volInput.max = '1';
    this.volInput.step = '0.01';
    this.volInput.value = '1';
    this.volInput.setAttribute('aria-label', 'Volume');
    this.volInput.addEventListener('input', () => {
      this.video.volume = Number(this.volInput.value);
      this.video.muted = this.video.volume === 0;
      this.syncVolume();
    });
    volBox.appendChild(this.volInput);
    volWrap.append(this.muteBtn, volBox);
    row.appendChild(volWrap);

    this.timeLabel = el('div', 'time');
    this.timeLabel.textContent = `0:00 / ${fmtTime(this.asset.duration)}`;
    row.appendChild(this.timeLabel);

    row.appendChild(el('div', 'control-spacer'));

    // --- settings menu ---
    const menuWrap = el('div', 'menu-wrap');
    this.menuBtn = el('button', 'cbtn', ICONS.settings) as HTMLButtonElement;
    this.menuBtn.setAttribute('aria-label', 'Settings');
    this.menuBtn.setAttribute('aria-haspopup', 'true');
    this.menuBtn.setAttribute('aria-expanded', 'false');
    this.menu = el('div', 'menu');
    this.menu.hidden = true;
    this.menuBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.menu.hidden = !this.menu.hidden;
      this.menuBtn.setAttribute('aria-expanded', String(!this.menu.hidden));
      if (!this.menu.hidden) this.renderMenu();
    });
    document.addEventListener('click', () => {
      if (!this.menu.hidden) {
        this.menu.hidden = true;
        this.menuBtn.setAttribute('aria-expanded', 'false');
      }
    });
    this.menu.addEventListener('click', (e) => e.stopPropagation());
    menuWrap.append(this.menu, this.menuBtn);
    row.appendChild(menuWrap);

    if ('requestPictureInPicture' in HTMLVideoElement.prototype) {
      const pip = el('button', 'cbtn', ICONS.pip) as HTMLButtonElement;
      pip.setAttribute('aria-label', 'Picture in picture');
      pip.addEventListener('click', () => {
        if (document.pictureInPictureElement) void document.exitPictureInPicture();
        else void this.video.requestPictureInPicture().catch(() => undefined);
      });
      row.appendChild(pip);
    }

    this.fsBtn = el('button', 'cbtn', ICONS.full) as HTMLButtonElement;
    this.fsBtn.setAttribute('aria-label', 'Fullscreen');
    this.fsBtn.addEventListener('click', () => void this.toggleFullscreen());
    row.appendChild(this.fsBtn);

    c.appendChild(row);
    return c;
  }

  private renderMenu(): void {
    const levels = this.hlsLevels();
    const frag = document.createDocumentFragment();

    if (levels.length > 1) {
      frag.appendChild(el('div', 'menu-label', 'Quality'));
      const mk = (label: string, value: 'auto' | number, note = '') => {
        const b = el('button');
        b.setAttribute('role', 'menuitemradio');
        b.setAttribute('aria-checked', String(this.quality === value));
        // Built as text nodes rather than innerHTML: labels are derived data,
        // and this keeps the menu safe by construction.
        const span = el('span');
        span.textContent = label;
        if (note) {
          const n = el('span');
          n.style.color = 'var(--muted-dim)';
          n.textContent = ` ${note}`;
          span.appendChild(n);
        }
        b.appendChild(span);
        b.addEventListener('click', () => {
          this.setQuality(value);
          this.menu.hidden = true;
          this.menuBtn.setAttribute('aria-expanded', 'false');
        });
        return b;
      };
      const autoNote = this.hls && this.hls.currentLevel >= 0 ? levels[this.hls.currentLevel]?.label ?? '' : '';
      frag.appendChild(mk('Auto', 'auto', autoNote));
      levels.forEach((l, i) => frag.appendChild(mk(l.label, i)));
    }

    frag.appendChild(el('div', 'menu-label', 'Speed'));
    for (const s of SPEEDS) {
      const b = el('button');
      b.setAttribute('role', 'menuitemradio');
      b.setAttribute('aria-checked', String(Math.abs(this.video.playbackRate - s) < 0.001));
      const sp = el('span');
      sp.textContent = s === 1 ? 'Normal' : `${s}\u00d7`;
      b.appendChild(sp);
      b.addEventListener('click', () => {
        this.video.playbackRate = s;
        this.renderMenu();
      });
      frag.appendChild(b);
    }

    this.menu.replaceChildren(frag);
  }

  private hlsLevels(): Rendition[] {
    if (this.hls) {
      return this.hls.levels.map((l) => ({
        label: `${l.height}p`,
        width: l.width,
        height: l.height,
        bitrateK: Math.round(l.bitrate / 1000),
      }));
    }
    return [];
  }

  private setQuality(q: 'auto' | number): void {
    this.quality = q;
    if (this.hls) this.hls.currentLevel = q === 'auto' ? -1 : q;
  }

  // -- media ---------------------------------------------------------------

  private async attachMedia(): Promise<void> {
    const hlsUrl = this.hooks.mediaUrl(this.asset.hls);
    const mp4Url = this.hooks.mediaUrl(this.asset.mp4);

    this.spinner.classList.add('show');

    // Order matters. Chrome answers "maybe" to canPlayType('...mpegurl') but
    // cannot actually play HLS, so trusting that check first leaves the video
    // stalled at readyState 0. Media Source Extensions is the real signal: if
    // MSE exists, hls.js drives playback; only a browser without it (iOS
    // Safari) gets the native path, which is also the one case where skipping
    // the ~185KB library is worth it.
    const choice = chooseSource({
      hasMse: hasMediaSource(),
      nativeHls: this.video.canPlayType('application/vnd.apple.mpegurl') !== '',
    });

    const Hls = choice === 'hls.js' ? (await import('hls.js')).default : null;
    if (this.disposed) return;

    if (Hls && Hls.isSupported()) {
      this.hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        // Keep a healthy buffer: these are short clips and viewers scrub a lot.
        maxBufferLength: 60,
        backBufferLength: 90,
        xhrSetup: (xhr) => {
          xhr.withCredentials = true;
        },
      });
      this.hls.loadSource(hlsUrl);
      this.hls.attachMedia(this.video);
      this.hls.on(Hls.Events.ERROR, (_e, data) => {
        if (!data.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          this.hls?.startLoad();
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          this.hls?.recoverMediaError();
        } else {
          // Unrecoverable: drop to the progressive file.
          this.hls?.destroy();
          this.hls = null;
          this.video.src = mp4Url;
          void this.video.play().catch(() => undefined);
        }
      });
    } else if (choice === 'native' || (choice === 'hls.js' && this.video.canPlayType('application/vnd.apple.mpegurl') !== '')) {
      // Either the browser plays HLS itself, or hls.js turned out unusable
      // after loading and native is still worth a try.
      this.video.src = hlsUrl;
    } else {
      this.video.src = mp4Url;
    }

    const v = this.video;
    v.addEventListener('play', () => {
      this.syncPlayState();
      this.showBigPlay(false);
      this.poster.classList.add('hide');
      this.lastTick = performance.now();
      this.hooks.onPlay?.(this.asset.id);
      this.nudgeUi();
    });
    v.addEventListener('pause', () => {
      this.syncPlayState();
      this.flushWatched();
      this.box.classList.remove('hide-ui');
      if (v.ended) this.showBigPlay(true);
    });
    v.addEventListener('ended', () => {
      this.playBtn.innerHTML = ICONS.replay;
      this.playBtn.classList.add('stroke');
      this.showBigPlay(true);
    });
    v.addEventListener('timeupdate', () => this.syncProgress());
    v.addEventListener('progress', () => this.syncBuffered());
    v.addEventListener('waiting', () => this.spinner.classList.add('show'));
    v.addEventListener('playing', () => this.spinner.classList.remove('show'));
    v.addEventListener('canplay', () => this.spinner.classList.remove('show'));
    v.addEventListener('volumechange', () => this.syncVolume());
    v.addEventListener('loadedmetadata', () => this.syncProgress());

    document.addEventListener('fullscreenchange', this.syncFullscreen);

    // Report watch time periodically so a viewer who closes the tab still counts.
    this.reportTimer = window.setInterval(() => this.flushWatched(), 10_000);

    // Autoplay is user-initiated (they clicked the hotspot), so it is allowed.
    void this.video.play().catch(() => this.showBigPlay(true));
  }

  // -- interaction ---------------------------------------------------------

  private bindScrub(): void {
    const pctFrom = (e: MouseEvent | Touch): number => {
      const r = this.scrub.getBoundingClientRect();
      return Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    };
    const seekTo = (p: number) => {
      const d = this.duration();
      if (d > 0) this.video.currentTime = p * d;
    };

    let dragging = false;
    this.scrub.addEventListener('mousedown', (e) => {
      dragging = true;
      seekTo(pctFrom(e));
      e.preventDefault();
    });
    window.addEventListener('mousemove', (e) => {
      if (dragging) seekTo(pctFrom(e));
    });
    window.addEventListener('mouseup', () => {
      dragging = false;
    });

    this.scrub.addEventListener('mousemove', (e) => {
      const p = pctFrom(e);
      const d = this.duration();
      this.hoverBar.style.width = `${p * 100}%`;
      this.tip.textContent = fmtTime(p * d);
      const r = this.scrub.getBoundingClientRect();
      this.tip.style.left = `${Math.min(Math.max(p * r.width, 26), r.width - 26)}px`;
    });
    this.scrub.addEventListener('mouseleave', () => {
      this.hoverBar.style.width = '0%';
    });

    this.scrub.addEventListener('touchstart', (e) => seekTo(pctFrom(e.touches[0]!)), { passive: true });
    this.scrub.addEventListener('touchmove', (e) => seekTo(pctFrom(e.touches[0]!)), { passive: true });

    this.scrub.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.seek(-5);
        e.preventDefault();
      } else if (e.key === 'ArrowRight') {
        this.seek(5);
        e.preventDefault();
      }
    });
  }

  private bindKeys(): void {
    this.onKey = (e: KeyboardEvent) => {
      if (this.disposed) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;

      switch (e.key) {
        case ' ':
        case 'k':
          this.toggle();
          break;
        case 'Escape':
          if (document.fullscreenElement) void document.exitFullscreen();
          else this.close();
          break;
        case 'ArrowLeft':
          this.seek(-5);
          break;
        case 'ArrowRight':
          this.seek(5);
          break;
        case 'j':
          this.seek(-10);
          break;
        case 'l':
          this.seek(10);
          break;
        case 'ArrowUp':
          this.bumpVolume(0.1);
          break;
        case 'ArrowDown':
          this.bumpVolume(-0.1);
          break;
        case 'm':
          this.toggleMute();
          break;
        case 'f':
          void this.toggleFullscreen();
          break;
        case 'Home':
          this.video.currentTime = 0;
          break;
        case 'End':
          this.video.currentTime = this.duration();
          break;
        default:
          if (/^[0-9]$/.test(e.key)) {
            this.video.currentTime = (Number(e.key) / 10) * this.duration();
          } else {
            return;
          }
      }
      e.preventDefault();
      e.stopPropagation();
      this.nudgeUi();
    };
    // Capture phase so the deck's own arrow-key navigation never fires while
    // the player is open.
    document.addEventListener('keydown', this.onKey, true);
  }

  private duration(): number {
    return Number.isFinite(this.video.duration) && this.video.duration > 0 ? this.video.duration : this.asset.duration;
  }

  private toggle(): void {
    if (this.video.ended) {
      this.video.currentTime = 0;
      this.playBtn.classList.remove('stroke');
    }
    if (this.video.paused) void this.video.play().catch(() => undefined);
    else this.video.pause();
  }

  private seek(delta: number): void {
    this.video.currentTime = Math.min(this.duration(), Math.max(0, this.video.currentTime + delta));
  }

  private bumpVolume(d: number): void {
    this.video.muted = false;
    this.video.volume = Math.min(1, Math.max(0, this.video.volume + d));
  }

  private toggleMute(): void {
    this.video.muted = !this.video.muted;
  }

  private async toggleFullscreen(): Promise<void> {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await this.box.requestFullscreen();
    } catch {
      /* user gesture or permissions — nothing useful to do */
    }
  }

  private nudgeUi(): void {
    this.box.classList.remove('hide-ui');
    window.clearTimeout(this.hideTimer);
    if (!this.video.paused) {
      this.hideTimer = window.setTimeout(() => {
        if (!this.video.paused && this.menu.hidden) this.box.classList.add('hide-ui');
      }, 2600);
    }
  }

  private showBigPlay(show: boolean): void {
    this.bigPlay.classList.toggle('hide', !show);
  }

  // -- view sync -----------------------------------------------------------

  private syncPlayState(): void {
    const playing = !this.video.paused && !this.video.ended;
    this.playBtn.innerHTML = playing ? ICONS.pause : ICONS.play;
    this.playBtn.classList.remove('stroke');
    this.playBtn.setAttribute('aria-label', playing ? 'Pause' : 'Play');
    this.showBigPlay(!playing);
  }

  private syncProgress(): void {
    const d = this.duration();
    const p = d > 0 ? this.video.currentTime / d : 0;
    this.played.style.width = `${p * 100}%`;
    this.knob.style.left = `${p * 100}%`;
    this.timeLabel.textContent = `${fmtTime(this.video.currentTime)} / ${fmtTime(d)}`;
    this.scrub.setAttribute('aria-valuenow', String(Math.round(p * 100)));
    this.scrub.setAttribute('aria-valuetext', `${fmtTime(this.video.currentTime)} of ${fmtTime(d)}`);

    const now = performance.now();
    if (!this.video.paused && this.lastTick) {
      this.watchAccum += (now - this.lastTick) / 1000;
    }
    this.lastTick = now;
    this.syncBuffered();
  }

  private syncBuffered(): void {
    const d = this.duration();
    const b = this.video.buffered;
    if (!d || b.length === 0) return;
    const t = this.video.currentTime;
    for (let i = 0; i < b.length; i++) {
      if (b.start(i) <= t && t <= b.end(i)) {
        this.buffered.style.width = `${(b.end(i) / d) * 100}%`;
        return;
      }
    }
    this.buffered.style.width = `${(b.end(b.length - 1) / d) * 100}%`;
  }

  private syncVolume(): void {
    const v = this.video.muted ? 0 : this.video.volume;
    this.volInput.value = String(v);
    this.muteBtn.innerHTML = v === 0 ? ICONS.volMute : v < 0.5 ? ICONS.volLow : ICONS.volHigh;
    this.muteBtn.setAttribute('aria-label', v === 0 ? 'Unmute' : 'Mute');
  }

  private syncFullscreen = (): void => {
    const on = document.fullscreenElement === this.box;
    this.fsBtn.innerHTML = on ? ICONS.exitFull : ICONS.full;
    this.fsBtn.setAttribute('aria-label', on ? 'Exit fullscreen' : 'Fullscreen');
  };

  private flushWatched(): void {
    if (this.watchAccum >= 1) {
      this.hooks.onWatched?.(this.asset.id, Math.round(this.watchAccum * 10) / 10);
      this.watchAccum = 0;
    }
  }

  // -- teardown ------------------------------------------------------------

  close(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.video.pause();
    this.flushWatched();
    window.clearInterval(this.reportTimer);
    window.clearTimeout(this.hideTimer);
    document.removeEventListener('keydown', this.onKey, true);
    document.removeEventListener('fullscreenchange', this.syncFullscreen);
    if (document.fullscreenElement === this.box) void document.exitFullscreen().catch(() => undefined);
    this.hls?.destroy();
    this.hls = null;
    this.video.removeAttribute('src');
    this.video.load();
    this.overlay.remove();
    this.onClose();
  }
}

export function openPlayer(asset: VideoAsset, hooks: PlayerHooks, onClose: () => void): Player {
  return new Player(asset, hooks, onClose);
}
